import React, { useMemo } from 'react';
import { Tag, Typography, Tooltip } from 'antd';
import useOpenApiParser from '../../../../../hooks/useOpenApiParser';
import './EndpointMethodPath.css';

const { Text } = Typography;

/**
 * EndpointMethodPath - Displays the HTTP method badge and full API URL
 * Constructs URL from server base URL + path, replacing variables with domain tags
 * Shows ellipsis for overflow with full URL on hover tooltip
 *
 * @param {string} method - The HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {Object} endpointUrl - Parsed endpoint URL data from parseEndpointUrl
 */
const EndpointMethodPath = ({ method, endpointUrl }) => {
  const { getMethodStyles } = useOpenApiParser();
  const methodStyles = getMethodStyles(method);

  if (!endpointUrl) {
    return null;
  }

  // Generate full URL text for tooltip
  const fullUrlText = useMemo(() => {
    let url = endpointUrl.serverUrl || endpointUrl.baseUrl || '';
    return url + (endpointUrl.path || '');
  }, [endpointUrl]);

  // Build the URL parts with variable tags
  const renderUrlWithVariables = () => {
    const parts = [];
    let currentUrl = endpointUrl.serverUrl || endpointUrl.baseUrl || '';

    // Early return if no URL is available
    if (!currentUrl && !endpointUrl.path) {
      return null;
    }

    // If we have variables, we need to replace them with tags
    if (endpointUrl.variables && endpointUrl.variables.length > 0) {
      let lastIndex = 0;

      endpointUrl.variables.forEach((variable, idx) => {
        // Find the position of this variable in the URL
        const varIndex = currentUrl.indexOf(variable.placeholder, lastIndex);

        if (varIndex !== -1) {
          // Add text before the variable
          if (varIndex > lastIndex) {
            parts.push(
              <span key={`text-${idx}`} className="endpoint-url-part">
                {currentUrl.substring(lastIndex, varIndex)}
              </span>
            );
          }

          // Add the variable as a tag
          parts.push(
            <Tag
              key={`var-${idx}`}
              className="endpoint-domain-tag"
              style={{
                backgroundColor: '#F0F5FF',
                border: 'none',
                color: '#2F54EB',
                borderRadius: '2px',
                fontSize: '10px',
                fontWeight: 400,
                padding: '1px 4px',
                margin: 0,
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: '16px',
                height: '18px',
                flexShrink: 0,
              }}
            >
              {`{${variable.name}}`}
            </Tag>
          );

          lastIndex = varIndex + variable.placeholder.length;
        }
      });

      // Add remaining text after last variable
      if (lastIndex < currentUrl.length) {
        parts.push(
          <span key="text-end" className="endpoint-url-part">
            {currentUrl.substring(lastIndex)}
          </span>
        );
      }
    } else {
      // No variables, just display the base URL
      if (currentUrl) {
        parts.push(
          <span key="baseurl" className="endpoint-url-part">
            {currentUrl}
          </span>
        );
      }
    }

    // Add the path
    parts.push(
      <span key="path" className="endpoint-url-part">
        {endpointUrl.path}
      </span>
    );

    return parts;
  };

  return (
    <div className="endpoint-method-path">
      <div className="endpoint-method-path-container">
        {/* Method Badge */}
        <Tag
          className="endpoint-method-badge"
          style={{
            backgroundColor: methodStyles.backgroundColor,
            border: `1px solid ${methodStyles.borderColor}`,
            color: methodStyles.color,
            borderRadius: '2px',
            fontSize: '12px',
            fontWeight: 500,
            padding: '2px 8px',
            margin: 0,
            flexShrink: 0,
          }}
        >
          {method?.toUpperCase()}
        </Tag>

        {/* Full URL with variable tags and tooltip */}
        <Tooltip title={fullUrlText} placement="topLeft">
          <div className="endpoint-url-container">{renderUrlWithVariables()}</div>
        </Tooltip>
      </div>
    </div>
  );
};

export default EndpointMethodPath;
